import { useState, useEffect } from 'react';
import { LayoutGrid, Brain, GitBranch, Trello, Target, Lightbulb, FileText, X, Plus, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const categoryIcons = {
  brainstorm: Brain,
  process: GitBranch,
  project: Trello,
  strategy: Target,
  meeting: FileText,
};

const categoryColors = {
  brainstorm: 'bg-purple-100 text-purple-600 border-purple-200',
  process: 'bg-blue-100 text-blue-600 border-blue-200',
  project: 'bg-green-100 text-green-600 border-green-200',
  strategy: 'bg-orange-100 text-orange-600 border-orange-200',
  meeting: 'bg-pink-100 text-pink-600 border-pink-200',
};

export const TemplatesModal = ({ isOpen, onClose, onApplyTemplate, whiteboardId, token, onSaveFirst }) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    let currentWhiteboardId = whiteboardId;
    
    // If whiteboard is new, save it first
    if (!whiteboardId || whiteboardId === 'new') {
      if (onSaveFirst) {
        toast.info('Saving whiteboard first...');
        try {
          currentWhiteboardId = await onSaveFirst();
          if (!currentWhiteboardId) {
            toast.error('Failed to save whiteboard');
            return;
          }
        } catch (error) {
          toast.error('Failed to save whiteboard');
          return;
        }
      } else {
        toast.error('Please save the whiteboard first');
        return;
      }
    }

    setApplying(templateId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whiteboards/${currentWhiteboardId}/apply-template?template_id=${templateId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onApplyTemplate && onApplyTemplate();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to apply template');
      }
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error('Failed to apply template');
    } finally {
      setApplying(null);
    }
  };

  const categories = ['all', ...new Set(templates.map(t => t.category))];
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const getCategoryIcon = (category) => {
    const Icon = categoryIcons[category] || LayoutGrid;
    return Icon;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col" data-testid="templates-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <LayoutGrid size={24} className="text-indigo-500" />
            Templates Library
          </DialogTitle>
        </DialogHeader>

        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap border-b pb-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all capitalize ${
                selectedCategory === category
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              No templates found in this category
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => {
                const CategoryIcon = getCategoryIcon(template.category);
                return (
                  <div
                    key={template.id}
                    className="group relative bg-white border rounded-xl p-4 hover:shadow-lg hover:border-indigo-300 transition-all cursor-pointer"
                    onClick={() => handleApplyTemplate(template.id)}
                    data-testid={`template-${template.id}`}
                  >
                    {/* Thumbnail */}
                    <div className={`h-24 rounded-lg mb-3 flex items-center justify-center text-4xl ${categoryColors[template.category] || 'bg-gray-100'}`}>
                      {template.thumbnail || <CategoryIcon size={40} />}
                    </div>
                    
                    {/* Info */}
                    <h3 className="font-semibold text-gray-800 mb-1">{template.name}</h3>
                    <p className="text-xs text-gray-500 line-clamp-2">{template.description}</p>
                    
                    {/* Category Badge */}
                    <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${categoryColors[template.category] || 'bg-gray-100 text-gray-600'}`}>
                      {template.category}
                    </div>
                    
                    {/* Apply Overlay */}
                    <div className="absolute inset-0 bg-indigo-500/90 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      {applying === template.id ? (
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      ) : (
                        <div className="text-white text-center">
                          <Plus size={24} className="mx-auto mb-1" />
                          <span className="text-sm font-medium">Apply Template</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t pt-3 flex justify-between items-center text-sm text-gray-500">
          <span>{filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
